import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Package, BarChart3, MapPinned, Layers } from "lucide-react";
import PageHero from "@/components/site/PageHero";
import CTAStrip from "@/components/site/CTAStrip";
import RelatedLinks from "@/components/site/RelatedLinks";
import { useSEO, useJsonLd, SITE_URL } from "@/lib/seo";
import { GoAndTrackLink, linkifyGoAndTrack } from "@/lib/goAndTrack";

const IMG = "/images/ll100-on-box.jpg";

export default function GpsTrackerForPackages() {
  useSEO({
    title:
      "GPS Tracker for Packages | Package Tracking Device & Shipment Tracking Devices",
    description:
      "GPS tracker for packages built for one shipment journey. Disposable package tracking devices for logistics, high-value, and pilot use cases.",
    canonical: "/gps-tracker-for-packages",
  });

  useJsonLd("service-gps-package", {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "GPS Tracker for Packages",
    alternateName: [
      "Package Tracking Device",
      "GPS Package Tracking Device",
      "Tracking Device for Packages",
      "Small Tracking Device for Packages",
      "Shipment Tracking Device",
    ],
    serviceType: "Package Tracking Device / Shipment Tracking Devices",
    category: "Package Tracking Device / Shipment Tracking Devices",
    description:
      "Single-use, package-level tracking devices for shipment monitoring. Designed for one journey, with no hardware recovery, recharging, or pairing required.",
    provider: {
      "@type": "Organization",
      name: "Disposable Smart Labels",
      url: SITE_URL,
    },
    url: `${SITE_URL}/gps-tracker-for-packages`,
    audience: {
      "@type": "BusinessAudience",
      audienceType: "Logistics, supply chain, and operations teams",
    },
    areaServed: [
      { "@type": "Place", name: "United Kingdom" },
      { "@type": "Place", name: "European Union" },
    ],
  });

  return (
    <div data-testid="page-gps-package">
      <PageHero
        eyebrow="Package-level tracking"
        title="GPS Tracker for Packages — Single-Use, No Recovery"
        subtitle="A package tracking device built for one shipment journey. Disposable shipment tracking devices that travel with the package, deliver clean tracking data, and never need to come back."
      >
        <Link to="/request-a-trial" className="btn-primary" data-testid="page-cta-trial">
          Speak to an expert <ArrowRight className="h-4 w-4" />
        </Link>
        <Link
          to="/disposable-gps-tracker"
          className="btn-secondary"
          data-testid="page-cta-disposable"
        >
          About disposable GPS trackers
        </Link>
      </PageHero>

      <section className="section">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <p className="eyebrow mb-4">What it is</p>
            <h2 className="h2">
              A package-level tracking device, designed to be discarded at delivery.
            </h2>
            <p className="lead mt-5">
              Most operational teams searching for a{" "}
              <strong className="text-ink">GPS tracker for packages</strong> are
              not actually looking for a long-life fleet of hardware. They want
              a small tracking device for packages that delivers one clean
              dataset per shipment and goes away.
            </p>
            <ul className="mt-8 space-y-3.5">
              {[
                "Applied directly to the package, carton, or pallet on the dispatch floor.",
                "Single-use — no recovery, no charging, no pairing process.",
                "Industry terms include GPS package tracking devices, tracking device for packages, and shipment tracking devices.",
                "Live shipment monitoring through GoAndTrack — without standing up new infrastructure.",
              ].map((b) => (
                <li key={b} className="flex items-start gap-3 text-[15px] text-ink">
                  <CheckCircle2 className="h-4 w-4 mt-1 text-brand shrink-0" />
                  {linkifyGoAndTrack(b)}
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-6">
            <div className="border border-line bg-white">
              <div className="aspect-[5/4] overflow-hidden">
                <img
                  src={IMG}
                  alt="Disposable package tracking device applied to a shipment carton"
                  className="h-full w-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section bg-surface-subtle border-y border-line">
        <div className="container-x">
          <div className="max-w-2xl mb-12">
            <p className="eyebrow mb-4">Characteristics</p>
            <h2 className="h2">What defines a good package tracking device.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-line border border-line">
            {[
              {
                icon: Package,
                k: "Package-form-factor",
                v: "Thin, lightweight, and small — sized for cartons, parcels, and pallets, not industrial enclosures.",
              },
              {
                icon: MapPinned,
                k: "Location and dwell data",
                v: "Captures location, dwell, and exception data at the shipment level — not just at handover points.",
              },
              {
                icon: BarChart3,
                k: "Operationally useful",
                v: "Data flows into GoAndTrack for live shipment monitoring, alerts, and exception workflows.",
              },
              {
                icon: Layers,
                k: "Single-use cost model",
                v: "Per-shipment OpEx, not per-fleet CapEx. Lost-tracker exposure stays bounded.",
              },
            ].map((b) => (
              <div key={b.k} className="bg-white p-7 lg:p-8">
                <b.icon className="h-5 w-5 text-brand" />
                <h3 className="h3 mt-4">{b.k}</h3>
                <p className="body-text mt-2 text-sm">{linkifyGoAndTrack(b.v)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container-x">
          <div className="max-w-2xl mb-12">
            <p className="eyebrow mb-4">Vocabulary</p>
            <h2 className="h2">
              The same device, called many different things.
            </h2>
            <p className="lead mt-5">
              Operations, supply chain, innovation, and procurement teams each
              tend to use slightly different language. The product underneath
              is the same.
            </p>
          </div>
          <div className="border border-line bg-white">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-line">
              {[
                ["GPS tracker for packages", "Procurement teams searching for hardware."],
                ["Package tracking device", "Operations teams scoping infrastructure."],
                ["GPS package tracking devices", "Buyer's-guide and RFP language."],
                ["Tracking device for packages", "Generic operational vocabulary."],
                ["Small tracking devices for packages", "Logistics teams emphasising form factor."],
                ["Disposable smart label", "Industry / supplier-side terminology."],
              ].map(([term, ctx]) => (
                <div key={term} className="p-6">
                  <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-brand">
                    Term
                  </p>
                  <p className="mt-2 font-heading text-base font-semibold text-ink">
                    {term}
                  </p>
                  <p className="mt-2 text-sm text-ink-muted">{ctx}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="section bg-ink text-white">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-white/60 mb-4">
              Shipment monitoring
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight leading-[1.1]">
              Tracking the device is not the goal. Tracking the shipment is.
            </h2>
            <p className="mt-6 text-white/70 max-w-2xl">
              A package tracking device is only useful if the data it produces
              ends up somewhere operationally meaningful. <GoAndTrackLink /> delivers
              that layer — live tracking, configurable alerts, and exception
              workflows that turn raw tracking data into operational decisions.
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="flex flex-wrap gap-3">
              <Link
                to="/request-a-trial"
                className="btn-primary bg-white text-ink hover:bg-white/90"
                data-testid="gps-package-cta-trial"
              >
                Speak to an expert <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/disposable-vs-reusable-trackers"
                className="btn-secondary bg-transparent text-white border-white/30 hover:bg-white/10 hover:border-white"
                data-testid="gps-package-cta-vs"
              >
                Compare with reusable trackers
              </Link>
            </div>
          </div>
        </div>
      </section>

      <RelatedLinks
        title="Related reading on package tracking"
        links={[
          { to: "/disposable-gps-tracker", label: "Disposable GPS Tracker — single-use tracking stickers" },
          { to: "/disposable-smart-labels", label: "Disposable Smart Labels — category overview" },
          { to: "/bluetooth-smart-labels", label: "Bluetooth Smart Labels — BLE technology explained" },
          { to: "/smart-labels-for-high-value-shipments", label: "Smart Labels for High-Value Shipments" },
        ]}
      />

      <CTAStrip
        title="Need a tracking device for a real package, not a slide deck?"
        body="A specialist will scope a pilot using disposable shipment tracking devices on a real lane."
      />
    </div>
  );
}
